import ObjectID from 'bson-objectid'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useContext, useEffect, useState } from 'react'
import { MuiOtpInput } from 'mui-one-time-password-input'

import { Breadcrumbs, Button, Typography } from '@mui/material'

import { FormError } from '../types'
import '../styles/pockets-management.css'
import { Pocket } from '../utils/dexie/db'
import { useOnlineStatus } from '../hooks'
import { countriesCurrencies } from '../lib'
// import { useSocket } from '../hooks/use-socket'
import Loader from '../components/common/loader'
import Dialog from '../components/common/dialog'
import { baseButtonBlueBg } from '../theme/custom-styles'
import DragIcon from '../assets/icons/drag-files-icon.svg'
import { useSecureStorage } from '../contexts/secure-storage.context'
import { checkTwoFactorAuth, requestAccessToken } from '../api/auth.api'
import { PocketItem } from '../components/pockets-management/pocket-item'
import { DeletePocketModal } from '../components/pockets-management/delete-pocket-modal'
import { CreatePocketModal } from '../components/pockets-management/create-pocket-modal'
import { TransferPocketModal } from '../components/pockets-management/transfer-pocket-modal'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../contexts'
import { PocketsTransfersModal } from '../components/pockets-management/pockets-transfers-modal'
import { saveTransferBetweenPockets, updateCustomerCompanyPocketData } from '../api/customers.api'
import { generateObjectId, handleCheckIfOtpCodeIsValid, handleEncryptMessage, numberWithCommas } from '../utils'
import { usePockets, addPocketLocally, deleteLocalPockets, deletePocketLocally, setDataWithOfflineStatus } from '../components/pockets-management/hooks/use-pockets'

const PocketsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toUpperCase()

  const navigate = useNavigate()
  const isOnline = useOnlineStatus()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { secret, handleGetSecret } = useSecureStorage()
  const { addPocketMutation, syncMutation } = usePockets(String(secret))
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['pockets-management', 'payments-to-review-management', 'two-factor-auth', 'common'])
  const { currentPocketsData, setCurrentPocketsData /* handleGetPocketsDataFromRemote,  handleGetCurrentPocketsData*/ } = useContext(AppStateContext)

  const [otpCode, setOtpCode] = useState('')
  const [errors, setErrors] = useState<FormError>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [pocketTransferData, setPocketTransferData] = useState<{
    amountToTransfer: number
    selectedPocketId: string
    pocketToTransferId: string
  } | null>(null)
  const [pocketDeletionData, setPocketDeletionData] = useState<{
    pocketId: string
    pocketToTransferId: string
  } | null>(null)
  const [selectedPocketId, setSelectedPocketId] = useState<string>('')
  const [currentPocketsBalance, setCurrentPocketsBalance] = useState<number>(0)
  const [openCreatePocketModal, setOpenCreatePocketModal] = useState<boolean>(false)
  const [openDeletePocketModal, setOpenDeletePocketModal] = useState<boolean>(false)
  const [openTransferPocketModal, setOpenTransferPocketModal] = useState<boolean>(false)
  const [openPocketsTransfersModal, setOpenPocketsTransfersModal] = useState<boolean>(false)
  const [createPocketModalIsLoading, setCreatePocketModalIsLoading] = useState<boolean>(false)
  const [dialogConfimationIsLoading, setDialogConfimationIsLoading] = useState<boolean>(false)
  const [openSendPaymentConfimationModal, setOpenSendPaymentConfimationModal] = useState<boolean>(false)

  useEffect(() => {
    return () => {
      deleteLocalPockets()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (Array.isArray(currentPocketsData) && currentPocketsData.length > 0) {
      let pocketsBalanceToSet = 0

      for (const element of currentPocketsData) {
        pocketsBalanceToSet += Number(element.balance)
      }

      setCurrentPocketsBalance(pocketsBalanceToSet)
      setIsLoading(false)
    }
  }, [currentPocketsData])

  useEffect(() => {
    if (!userSession || (userSession && userSession.role !== 'super-admin-client') || (userSession?.role === 'super-admin-client' && userSession.subRole)) {
      navigate('/404')
      // return
    }

    // handleGetSectionData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  // const handleGetSectionData = async () => {
  //   await handleGetCurrentPocketsData?.()
  //   setIsLoading(false)
  // }

  useEffect(() => {
    const handleOnline = async () => {
      const secretGenerated = handleGetSecret()
      syncMutation.mutate({ secret: String(secretGenerated), refreshToken: isAuth?.replaceAll('"', '') ?? '' })
    }

    const handleOffline = () => {
      setDataWithOfflineStatus()
    }

    globalThis.addEventListener('online', handleOnline)
    globalThis.addEventListener('offline', handleOffline)

    return () => {
      globalThis.removeEventListener('online', handleOnline)
      globalThis.removeEventListener('offline', handleOffline)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncMutation])

  const handleDeletePocket = async (pocketId: string, pocketToTransferId: string) => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setDialogConfimationIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      return setDialogConfimationIsLoading(false)
    }

    const currentDataOfPocketToDelete = currentPocketsData?.find((pocketData: Pocket) => String(pocketData.id) === String(pocketId))
    const currentDataOfPocketToTransfer = currentPocketsData?.find((pocketData: Pocket) => String(pocketData.id) === String(pocketToTransferId))

    const secretGenerated = handleGetSecret()
    const updatedBalanceZero = await handleEncryptMessage(String(Number(0)), String(secretGenerated))
    const updatedBalanceTransfered = await handleEncryptMessage(String(Number(currentDataOfPocketToTransfer?.balance) + Number(currentDataOfPocketToDelete?.balance)), String(secretGenerated))

    const pocketsUpdatedData = await deletePocketLocally(pocketId, pocketToTransferId, String(updatedBalanceTransfered), String(updatedBalanceZero), String(secretGenerated))
    setCurrentPocketsData(pocketsUpdatedData)

    if (isOnline) {
      syncMutation.mutate({ secret: String(secretGenerated), refreshToken: isAuth?.replaceAll('"', '') ?? '' })
    }

    updateNewNotificationStatus?.(true, 'pocket-deleted')

    setPocketDeletionData(null)
    setOpenSendPaymentConfimationModal(false)
    setDialogConfimationIsLoading(false)
    setOpenDeletePocketModal(false)
  }

  const handleCreatePocket = async (pocketNameData: string) => {
    setCreatePocketModalIsLoading(true)

    const secretGenerated = handleGetSecret()
    const newPocketToSave = {
      balance: 0,
      deleted: 0,
      main: false,
      name: pocketNameData,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      id: String(new ObjectID()),
      belongsTo: String(userSession?.belongsTo)
    }

    if (isOnline) {
      addPocketMutation.mutate({
        secret: String(secretGenerated),
        newPocket: newPocketToSave,
        refreshToken: isAuth?.replaceAll('"', '') ?? ''
      })
    } else {
      const pocketsUpdatedData = await addPocketLocally(newPocketToSave, String(secretGenerated))
      setCurrentPocketsData(pocketsUpdatedData)
    }

    updateNewNotificationStatus?.(true, 'pocket-created')
    setCreatePocketModalIsLoading(false)
    setOpenCreatePocketModal(false)
  }

  const handleTransferPocket = async (selectedPocketId: string, pocketToTransferId: string, amountToTransfer: number) => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setDialogConfimationIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      setOtpCode('')
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      return setDialogConfimationIsLoading(false)
    }

    const currentPocketToData = currentPocketsData?.find((pocketData: Pocket) => String(pocketData.id) === String(pocketToTransferId))
    const currentPocketFromData = currentPocketsData?.find((pocketData: Pocket) => String(pocketData.id) === String(selectedPocketId))

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      const pocketTransferId = generateObjectId()

      await saveTransferBetweenPockets(
        {
          amountToTransfer,
          pocketTransferId,
          currentPocketToData,
          currentPocketFromData,
          belongsTo: userSession?.belongsTo
        },
        accessToken
      )

      setOtpCode('')
      updateNewNotificationStatus?.(true, 'pocket-transfer')
    } catch (error: any) {
      setOtpCode('')
      console.log(error, error?.response)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.pocketTransferForbidden', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogConfimationIsLoading(false)
      return
    }

    setPocketTransferData(null)
    setOpenSendPaymentConfimationModal(false)
    setDialogConfimationIsLoading(false)
    setOpenTransferPocketModal(false)
  }

  const handleUpdatePocketName = async (pocketIdToSet: string, pocketNameToSet: string) => {
    try {
      setIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      await updateCustomerCompanyPocketData(pocketIdToSet, { name: pocketNameToSet }, accessToken)

      const currentPocketIndex = currentPocketsData?.findIndex((pocketData: Pocket) => String(pocketData.id) === String(pocketIdToSet)) || -1
      const pocketsUpdatedData = [...(currentPocketsData || [])]
      pocketsUpdatedData[currentPocketIndex] = {
        ...pocketsUpdatedData[currentPocketIndex],
        name: pocketNameToSet
      }

      setCurrentPocketsData(pocketsUpdatedData)
    } catch (error: any) {
      console.log(error, error?.response)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.pocketTransferForbidden', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    updateNewNotificationStatus?.(true, 'pocket-name-updated')
    setIsLoading(false)
    return true
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <>
      {isLoading && <Loader className="loading-section" />}
      <div className="section-container">
        <Breadcrumbs aria-label="breadcrumb">
          <Typography
            key="3"
            className="breadcrumb-active"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        </Breadcrumbs>
        <div className="banks-balances-title-container pockets-balances-title-container">
          <h1
            className="banks-balances-title"
            style={{ display: 'inline-flex' }}
          >
            {currency === 'BRL'
              ? `${t('titleTotalBalanceAvailable')}: $ ${numberWithCommas(Number(currentPocketsBalance))} ${currency}`
              : `${t('titleTotalBalanceAvailable')}: $ ${numberWithCommas(Math.round(Number(currentPocketsBalance)))} ${currency}`}
          </h1>
          <Button
            variant="contained"
            sx={baseButtonBlueBg}
            onClick={() => setOpenPocketsTransfersModal(true)}
          >
            <div className="account-management-profile-form-password-button-text">{t('seePocketsMovements')}</div>
          </Button>
        </div>
        <div className="pockets-full-container">
          {currentPocketsData && (
            <>
              {currentPocketsData.map((pocket: Pocket) => {
                return (
                  <PocketItem
                    pocket={pocket}
                    key={pocket._id}
                    handleUpdatePocketName={handleUpdatePocketName}
                    showMoreActionsButton={currentPocketsData.length > 1}
                    handleDeleteSeletedPocket={(pocketIdToSet: string) => {
                      setOpenDeletePocketModal(true)
                      setSelectedPocketId(pocketIdToSet)
                    }}
                    handleTransferSeletedPocket={(pocketIdToSet: string) => {
                      setOpenTransferPocketModal(true)
                      setSelectedPocketId(pocketIdToSet)
                    }}
                  />
                )
              })}
            </>
          )}
          <div
            onClick={() => {
              setOpenCreatePocketModal(true)
            }}
            className="add-pocket-button-container"
          >
            <img
              src={DragIcon}
              alt="Upload Files"
              className="drag-icon"
            />
            <p>
              <strong>{t('addButton')}</strong>
              <br />
              {t('clickHere')}
            </p>
          </div>
        </div>
      </div>
      {openTransferPocketModal && (
        <TransferPocketModal
          isLoading={false}
          selectedPocketId={selectedPocketId}
          currentPocketsData={currentPocketsData || []}
          handleTransferPocket={(selectedPocketId: string, pocketToTransferId: string, amountToTransfer: number) => {
            setPocketTransferData({
              amountToTransfer,
              selectedPocketId,
              pocketToTransferId
            })

            setOpenSendPaymentConfimationModal(true)
          }}
          handleOpenStateOfModal={(openStateToSet: boolean) => setOpenTransferPocketModal(openStateToSet)}
        />
      )}
      {openCreatePocketModal && (
        <CreatePocketModal
          isLoading={createPocketModalIsLoading}
          handleCreatePocket={handleCreatePocket}
          handleOpenStateOfModal={(openStateToSet: boolean) => setOpenCreatePocketModal(openStateToSet)}
        />
      )}
      {openDeletePocketModal && (
        <DeletePocketModal
          isLoading={false}
          selectedPocketId={selectedPocketId}
          handleDeletePocket={(pocketId: string, pocketToTransferId: string) => {
            setPocketDeletionData({
              pocketId,
              pocketToTransferId
            })

            setOpenSendPaymentConfimationModal(true)
          }}
          currentPocketsData={currentPocketsData || []}
          handleOpenStateOfModal={(openStateToSet: boolean) => setOpenDeletePocketModal(openStateToSet)}
        />
      )}
      {openSendPaymentConfimationModal && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle
          handleDialogAction={() => {
            if (pocketTransferData) {
              handleTransferPocket(pocketTransferData.selectedPocketId, pocketTransferData.pocketToTransferId, pocketTransferData.amountToTransfer)
            }

            if (pocketDeletionData) {
              handleDeletePocket(pocketDeletionData.pocketId, pocketDeletionData.pocketToTransferId)
            }
          }}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setOtpCode('')
              setPocketTransferData(null)
              setPocketDeletionData(null)
            }

            setOpenSendPaymentConfimationModal(openState)
          }}
          dialogIsLoading={dialogConfimationIsLoading}
          dialogTitle={t('confirmSendPaymentModal.title', { ns: 'payments-to-review-management' })}
          actionButtonText={t('confirmSendPaymentModal.actionButtonTextTransfer')}
        >
          <div
            className="step"
            style={{ marginTop: '0px', gap: '16px' }}
          >
            <div
              className="step-instruction-container"
              style={{ fontSize: '14px', color: '#F59E0B' }}
            >
              {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
            </div>
            <MuiOtpInput
              length={6}
              value={otpCode}
              onChange={(newValue: string) => setOtpCode(newValue)}
              className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
            />
          </div>
        </Dialog>
      )}
      {openPocketsTransfersModal && <PocketsTransfersModal handleSetModalOpenState={(openState: boolean) => setOpenPocketsTransfersModal(openState)} />}
    </>
  )
}

export default PocketsManagement
